import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';

const Contact = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();
  const { t } = useLanguage();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
          email,
          subject,
          message
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to send message');
      }
      
      toast({
        title: t('contact.successTitle'),
        description: t('contact.successDesc'),
      });
      // Reset form
      setName('');
      setEmail('');
      setSubject('');
      setMessage('');
    } catch (error) {
      console.error('Contact form error:', error);
      toast({
        title: "Failed to Send",
        description: error.message || "Failed to send your message. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-24 pb-16">
      <SEO title="Contact Jerusalem Holy Prayer — Questions About Your Prayer Request" description="Contact us about your online prayer request to Jerusalem's holy sites. Reach our team for questions about delivery, photo proof, or the Church of the Holy Sepulchre and Western Wall service." path="/contact" />
      {/* Hero Section */}
      <div className="container mx-auto px-4 mb-16">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full shadow-xl">
              <Mail className="h-12 w-12 text-white" />
            </div>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            {t('contact.title')}
          </h1>
          <p className="text-xl text-gray-600">
            {t('contact.subtitle')}
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4">
        {/* Before You Contact Section */}
        <div className="max-w-4xl mx-auto mb-12">
          <Card className="border-2 border-blue-200 bg-blue-50">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('contactPage.beforeReachOut')}</h2>
              <div className="space-y-4 text-gray-700">
                <p className="font-medium">{t('contactPage.forFasterAssistance')}</p>
                <ul className="space-y-2 ml-6">
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2 mt-1">→</span>
                    <span><a href="/faq" className="text-blue-600 hover:text-blue-800 font-medium underline">{t('contactPage.faqLink')}</a> {t('contactPage.faqDesc')}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2 mt-1">→</span>
                    <span><a href="/about" className="text-blue-600 hover:text-blue-800 font-medium underline">{t('contactPage.aboutLink')}</a> {t('contactPage.aboutDesc')}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2 mt-1">→</span>
                    <span><a href="/terms" className="text-blue-600 hover:text-blue-800 font-medium underline">{t('contactPage.termsLink')}</a> {t('contactPage.termsDesc')}</span>
                  </li>
                </ul>
                <p className="mt-4 text-sm italic">
                  {t('contactPage.technicalNote')}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Information */}
          <div className="lg:col-span-1 space-y-6">
            <Card className="border-2 border-gray-200 hover:shadow-xl transition-all">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-amber-100 rounded-lg">
                    <Mail className="h-6 w-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{t('contact.emailUs')}</h3>
                    <p className="text-gray-600 text-sm mb-1">{t('contactPage.generalInquiries')}</p>
                    <p className="text-amber-600 text-sm font-medium mb-2">info@jerusalemholyprayer.org</p>
                    <p className="text-gray-600 text-sm mb-1">{t('contactPage.prayerSupport')}</p>
                    <p className="text-amber-600 text-sm font-medium mb-2">prayers@jerusalemholyprayer.org</p>
                    <p className="text-gray-600 text-sm mb-1">{t('contactPage.technicalSupport')}</p>
                    <p className="text-amber-600 text-sm font-medium">support@jerusalemholyprayer.org</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-200 hover:shadow-xl transition-all">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-amber-100 rounded-lg">
                    <Phone className="h-6 w-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{t('contactPage.callUs')}</h3>
                    <p className="text-gray-600 text-sm mb-2">Phone support coming soon</p>
                    <p className="text-gray-500 text-xs mt-2">
                      For now, please contact us via email
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-200 hover:shadow-xl transition-all">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-amber-100 rounded-lg">
                    <MapPin className="h-6 w-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{t('contactPage.officeLocation')}</h3>
                    <p className="text-gray-600 text-sm mb-1">{t('contactPage.officeAddress1')}</p>
                    <p className="text-gray-600 text-sm mb-1">{t('contactPage.officeAddress2')}</p>
                    <p className="text-gray-600 text-sm mb-2">{t('contactPage.officeAddress3')}</p>
                    <p className="text-gray-500 text-xs mt-2">
                      {t('contactPage.officeNote')}<br/>
                      {t('contactPage.officeVisit')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-amber-200 bg-amber-50">
              <CardContent className="p-6">
                <h3 className="font-semibold text-gray-900 mb-3">{t('contactPage.responseTimes')}</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start">
                    <span className="text-amber-600 mr-2">•</span>
                    <span>{t('contactPage.responseEmail')}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-amber-600 mr-2">•</span>
                    <span>{t('contactPage.responsePrayer')}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-amber-600 mr-2">•</span>
                    <span>{t('contactPage.responsePhone')}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-amber-600 mr-2">•</span>
                    <span>{t('contactPage.responseUrgent')}</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="border-2 border-gray-200 shadow-xl">
              <CardContent className="p-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">{t('contact.send')}</h2>
                <p className="text-gray-600 mb-6">
                  {t('contactPage.formIntro')}
                </p>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name">{t('contact.name')} *</Label>
                      <Input
                        id="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        className="border-2 border-gray-300 focus:border-amber-600"
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">{t('contact.email')} *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="border-2 border-gray-300 focus:border-amber-600"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="subject">{t('contact.subject')} *</Label>
                    <Input
                      id="subject"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      required
                      className="border-2 border-gray-300 focus:border-amber-600"
                      placeholder="How can we help you?"
                    />
                  </div>
                  <div>
                    <Label htmlFor="message">{t('contact.message')} *</Label>
                    <Textarea
                      id="message"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      required
                      className="min-h-40 border-2 border-gray-300 focus:border-amber-600"
                      placeholder="Tell us more about your inquiry..."
                    />
                  </div>
                  <Button 
                    type="submit" 
                    disabled={submitting}
                    className="w-full bg-amber-600 hover:bg-amber-700 text-white py-6 text-lg"
                    data-testid="contact-submit-btn"
                  >
                    {submitting ? (
                      <>{t('contact.sending')}</>
                    ) : (
                      <>
                        <Send className="mr-2" size={20} />
                        {t('contact.send')}
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;